import { useEffect, useState } from "react";
import Lenis from "lenis";
import config from "../data/weddingConfig.json";
import Cursor from "../components/Cursor";
import ScrollProgress from "../components/ScrollProgress";
import Loader from "../components/Loader";
import Hero from "../components/Hero";
import Chapter1Timeline, { StoryMarquee } from "../components/Chapter1Timeline"; // eslint-disable-line no-unused-vars
import Chapter2Countdown from "../components/Chapter2Countdown";
import Chapter3Details from "../components/Chapter3Details";
import Chapter4Venue from "../components/Chapter4Venue";
import Chapter5Gallery from "../components/Chapter5Gallery";
import Chapter6Music from "../components/Chapter6Music"; // eslint-disable-line no-unused-vars
import Chapter7RSVP from "../components/Chapter7RSVP";
import Chapter8Gifts from "../components/Chapter8Gifts";
import Chapter9Finale from "../components/Chapter9Finale";

const NAV_LINKS = [
  { label: "Cuenta regresiva", short: "Cuenta reg.", target: "chapter-2", mobile: true },
  { label: "Detalles", target: "chapter-3" },
  { label: "El lugar", target: "chapter-4", mobile: true },
  { label: "Mesa de regalos", target: "chapter-8" },
  { label: "Confirmar", target: "chapter-7", mobile: true },
];

export default function Experience() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;

    const lenis = new Lenis({
      duration: 1.15,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
      syncTouch: false,
      wheelMultiplier: 1,
      touchMultiplier: 1.2,
      lerp: 0.1,
    });
    window.__lenis = lenis;

    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);

    const onScroll = ({ scroll }) => setScrolled(scroll > 40);
    lenis.on("scroll", onScroll);

    const safety = setTimeout(() => {
      lenis.scrollTo(0, { immediate: true });
    }, 60);

    return () => {
      clearTimeout(safety);
      cancelAnimationFrame(raf);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  const goTo = (target) => {
    const el = document.querySelector(`[data-testid='${target}']`);
    if (!el) return;
    if (window.__lenis) {
      window.__lenis.scrollTo(el, { offset: -20, duration: 1.4 });
    } else {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const { partnerOne, partnerTwo } = config.couple;

  return (
    <div className="App grain relative bg-warm-white" data-testid="wedding-experience">
      <Loader names={`${partnerOne} \u0026 ${partnerTwo}`} />
      <Cursor />
      <ScrollProgress />

      {/* Editorial sticky nav */}
      <header
        className={`fixed left-0 top-0 z-[65] w-full transition-all duration-500 ${
          scrolled ? "bg-deep-black/60 backdrop-blur-md" : "bg-transparent"
        }`}
        data-testid="site-nav"
      >
        <div className="flex items-center justify-between px-4 py-3 sm:px-6 sm:py-4 md:px-12 md:py-6">
          <button
            onClick={() => goTo("hero-section")}
            className="font-serif text-base italic tracking-[0.05em] text-ivory mix-blend-difference sm:text-lg md:text-xl"
            data-testid="nav-logo"
          >
            {partnerOne[0]} <span className="not-italic text-gold">&amp;</span> {partnerTwo[0]}
          </button>

          {/* Desktop links (md+) */}
          <nav className="hidden items-center gap-10 md:flex">
            {NAV_LINKS.map((l) => (
              <button
                key={l.target}
                onClick={() => goTo(l.target)}
                data-testid={`nav-${l.target}`}
                className="group relative text-[10px] uppercase tracking-[0.35em] text-ivory mix-blend-difference"
              >
                {l.label}
                <span className="absolute -bottom-1 left-0 block h-px w-full origin-left scale-x-0 bg-gold transition-transform duration-500 group-hover:scale-x-100" />
              </button>
            ))}
          </nav>

          {/* Mobile links (<md) — 3 always-visible primary links */}
          <nav className="flex items-center gap-3 sm:gap-5 md:hidden">
            {NAV_LINKS.filter((l) => l.mobile).map((l) => (
              <button
                key={l.target}
                onClick={() => goTo(l.target)}
                data-testid={`nav-mobile-${l.target}`}
                className="text-[9px] font-medium uppercase tracking-[0.18em] text-ivory mix-blend-difference"
              >
                {l.short || l.label}
              </button>
            ))}
          </nav>
        </div>
      </header>

      <main>
        <Hero config={config} />
        <StoryMarquee />
        <Chapter2Countdown config={config} />
        <Chapter3Details config={config} />
        <Chapter4Venue config={config} />
        <Chapter5Gallery config={config} />
        <Chapter7RSVP />
        <Chapter8Gifts config={config} />
        <Chapter9Finale config={config} />
      </main>
    </div>
  );
}
